import { ChevronRight } from "lucide-react";
import {
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { Button } from "@src/shared/components/ui/button";
import { cn } from "@src/shared/lib/utils";

import { AgendaScratchPadView, useAgendaScratchpad } from "../AgendaScratchPad";

// SSR-safe: `apps/web` renders on the server, where `useLayoutEffect` warns.
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Whether collapsing `node` will actually run a `height` transition (versus
 * snapping instantly). False under `prefers-reduced-motion`, where Tailwind's
 * `motion-reduce:transition-none` zeroes out `transitionProperty` — in which
 * case `transitionend` never fires, so callers must not wait for it.
 */
function willAnimateHeight(node: HTMLElement): boolean {
  const style = getComputedStyle(node);
  const transitionsHeight = style.transitionProperty
    .split(",")
    .some((property) => property.trim() === "height");
  return transitionsHeight && Number.parseFloat(style.transitionDuration) > 0;
}

export interface AgendaDockProps {
  encounterId?: number;
  disabled?: boolean;
  /**
   * Live transcript content. Stays mounted (visually hidden) while the
   * transcript section is collapsed, so the section's height can animate on
   * expand/collapse — see `DockSection`'s doc comment. Undefined hides the
   * transcript toggle entirely (e.g. no live recording).
   */
  transcript?: ReactNode;
  /** Live line count shown in the collapsed strip's toggle label. */
  transcriptLineCount?: number;
  className?: string;
}

const SCRATCHPAD_LABEL = "Scratch pad";

/**
 * Height cap for the whole pinned scratchpad+transcript stack, as a
 * percentage of `AgendaDock`'s direct parent — the `<section>` in
 * Agenda.tsx, which `AgendaDock` is rendered directly inside. That
 * `<section>` is *not* `h-full` (it's `min-h-0 flex-1`); the percentage
 * still resolves against a real number instead of silently becoming `none`
 * because of a CSS flexbox rule, not an explicit height: once flex layout
 * has resolved a flex item's main-axis size, that size counts as *definite*
 * for descendants' percentage resolution, even though the item's specified
 * height was `auto`. The `<section>` is a `flex-1` item of its own
 * (definite-height) ancestor, so its post-flex height is definite, and this
 * `max-height: 60%` resolves against it. If a future refactor makes the
 * `<section>` (or `AgendaDock`'s parent generally) a plain block instead of
 * a flex item — or removes `flex-1` from it — this cap silently stops
 * clamping anything. This is Nacho's estimate of Ed's prototype intent, not
 * a derived constant — his caps are 50cqh (scratch pad) and 38cqh
 * (transcript) against a container that also holds the message composer;
 * this single number covers both panels combined against the simpler
 * agenda-column ancestor we actually have. Recheck at a couple of viewport
 * heights if the dock feels cramped or loose.
 */
const DOCK_MAX_HEIGHT_PERCENT = 60;

/**
 * Height of each section's toggle row (matches the `h-8` button below).
 * Used to translate "preferred body height" (what the numbers mean to a
 * reader comparing against Ed's prototype) into "preferred section height"
 * (what flexbox actually needs): the section's declared height is a
 * flex-basis, not an enforced size, so a mismatch here just shifts how much
 * of that basis the body actually gets — it doesn't break anything.
 *
 * Also doubles as the collapsed section's pre-measurement height (the value
 * used for the very first server-rendered paint, before `DockSection` can
 * measure its own trigger row via `ResizeObserver`). Once mounted, the
 * measured height takes over, so this constant only has to be "close enough"
 * to avoid a first-paint flash — it does not have to track `h-8` exactly.
 */
const DOCK_TOGGLE_ROW_HEIGHT_PX = 32;
/** Matches Ed's `.scribePad` height. */
const SCRATCHPAD_BODY_PREFERRED_HEIGHT_PX = 118;
/** Matches the flat-px branch of Ed's `.scribeTranscript` (`min(180px, 38cqh)`). */
const TRANSCRIPT_BODY_PREFERRED_HEIGHT_PX = 180;

interface DockSectionProps {
  isOpen: boolean;
  onToggle: () => void;
  triggerLabel: string;
  statusText?: string;
  contentId: string;
  preferredHeightPx: number;
  children: ReactNode;
  /**
   * Ed's prototype puts `borderBottom` on the composer row and the scratch
   * pad section, and no border on the transcript section (see
   * `visit.tsx:848-931` in the reference prototype). We only render it when
   * a section follows this one (`hasTranscript` for the scratch pad) so a
   * lone open section doesn't get a trailing divider with nothing to divide
   * — the literal Ed markup assumes both sections always exist, so this is
   * an interpretation of intent rather than a byte-for-byte port.
   */
  withBottomBorder?: boolean;
  /** Test hook only — identifies which section this is in Storybook play functions. */
  testId: string;
}

/**
 * One collapsible row of the dock: a chevron + label toggle, and its content
 * below. The section's own height is a flex-basis (the trigger row's height
 * when collapsed, `preferredHeightPx` when open), not a hard size — when
 * both sections are open and the pinned stack hits `DOCK_MAX_HEIGHT_PERCENT`,
 * flexbox shrinks these (`min-h-0` on both the section and its content) and
 * the content div's own `overflow-y-auto` keeps everything reachable by
 * scrolling.
 *
 * Content stays mounted in both states so the height change can be animated
 * (a snap-to-`display: none` can't transition) and, for the scratch pad,
 * so `AgendaScratchPadView`'s `EditorContent` never remounts across a
 * toggle — see `useAgendaScratchpad`'s doc comment. While collapsed the
 * content is `visibility: hidden`, which both removes it from the
 * accessibility tree and takes it out of tab order (unlike `overflow:
 * hidden`, which still leaves clipped content focusable). On collapse that
 * hiding is deliberately delayed until the height transition actually
 * finishes (`transitionend`, with a matching-duration timeout fallback) so
 * the content recedes along with the shrinking box instead of vanishing
 * before it — see `isVisible`'s effect below. On expand it's applied
 * immediately: nothing to wait for, and the content needs to be visible for
 * the reveal.
 */
function DockSection({
  isOpen,
  onToggle,
  triggerLabel,
  statusText,
  contentId,
  preferredHeightPx,
  children,
  withBottomBorder = false,
  testId,
}: DockSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeightPx, setHeaderHeightPx] = useState(
    DOCK_TOGGLE_ROW_HEIGHT_PX,
  );
  // Tracks ONLY the lagging hide on collapse — see the effect below. It is
  // deliberately not the whole story for visibility: see `isContentVisible`.
  const [isVisible, setIsVisible] = useState(isOpen);

  // `isOpen ||` is load-bearing, not redundant. `setIsVisible(true)` happens
  // in an effect, which runs *after* paint, so driving visibility off
  // `isVisible` alone left one painted frame on expand where the height was
  // already the expanded value while the content was still
  // `visibility: hidden` — an empty growing box. Under
  // `prefers-reduced-motion` the height snaps instead of animating, so that
  // frame showed a full-size empty box rather than a barely-started one.
  // Or-ing `isOpen` in makes the expand path visible in the same render that
  // grows the box, with no dependency on effect timing at all. Collapse
  // still lags: `isOpen` goes false while `isVisible` stays true until the
  // transition ends.
  const isContentVisible = isOpen || isVisible;

  // Measure the trigger row instead of hardcoding a second magic number for
  // it: the row's rendered height already matches `DOCK_TOGGLE_ROW_HEIGHT_PX`
  // today (it's `h-8`), but deriving it keeps the collapsed height correct
  // if that ever changes (font scaling, a longer label wrapping, etc.)
  // without anyone having to remember to update a constant in two places.
  useIsomorphicLayoutEffect(() => {
    const node = headerRef.current;
    if (!node) return;
    const measure = () =>
      setHeaderHeightPx(node.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // On expand this only re-arms `isVisible` for the NEXT collapse — showing
  // the content is already handled synchronously by `isContentVisible`
  // above, precisely because this effect runs too late to do it. It also
  // cancels any hide still pending from a collapse that got reopened
  // mid-transition: the cleanup below tears down the previous run's
  // listener/timeout before this one starts.
  //
  // Collapsing is the part that genuinely needs an effect — it waits for the
  // `height` transition to actually finish before hiding, so the content
  // recedes with the box instead of popping out ahead of it. Under
  // `prefers-reduced-motion` there's no transition to wait for
  // (`willAnimateHeight` is false), so it hides immediately instead of
  // sitting focusable-but-clipped for a phantom 200ms.
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      return;
    }
    const node = sectionRef.current;
    if (!node || !willAnimateHeight(node)) {
      setIsVisible(false);
      return;
    }
    const durationMs =
      Number.parseFloat(getComputedStyle(node).transitionDuration) * 1000;
    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target === node && event.propertyName === "height") {
        setIsVisible(false);
      }
    };
    node.addEventListener("transitionend", handleTransitionEnd);
    // Fallback in case `transitionend` doesn't fire (e.g. the element is
    // detached or the transition gets interrupted) — a small buffer over
    // the CSS duration so the real event wins the race when it does fire.
    const fallbackId = window.setTimeout(
      () => setIsVisible(false),
      durationMs + 50,
    );
    return () => {
      node.removeEventListener("transitionend", handleTransitionEnd);
      window.clearTimeout(fallbackId);
    };
  }, [isOpen]);

  return (
    <div
      ref={sectionRef}
      className={cn(
        // No `overflow-hidden` here: it would clip the trigger's
        // focus-visible ring (which paints outside the button, right at
        // this container's edge). The content div's own `overflow-y-auto`
        // already clips its overflowing children to the animating height —
        // per the CSS overflow spec, pairing `overflow-y: auto` with the
        // default `overflow-x: visible` computes `overflow-x` to `auto` too,
        // so horizontal overflow is contained the same way.
        "flex flex-col bg-background",
        "transition-[height] duration-200 ease-out motion-reduce:transition-none",
        isOpen ? "min-h-0" : "shrink-0",
        withBottomBorder && "border-b border-border",
      )}
      data-testid={`dock-section-${testId}`}
      style={{ height: isOpen ? preferredHeightPx : headerHeightPx }}
    >
      {/*
       * `z-10` keeps the trigger's focus-visible ring on top of the content
       * below it. The ring paints outside the button's box, overlapping the
       * content wrapper's first child — and the scratch pad's editor
       * container is itself `relative` (for its placeholder). Two positioned
       * elements both at `z-index: auto` paint in DOM order, so the editor,
       * coming later, drew over the ring's bottom edge and the outline
       * appeared to run underneath it.
       */}
      <div ref={headerRef} className="relative z-10 shrink-0">
        <Button
          type="button"
          variant="ghost"
          className="h-8 w-full justify-start gap-1.5 rounded-none px-3 text-[12px] font-medium text-muted-foreground hover:bg-muted/60"
          aria-expanded={isOpen}
          aria-controls={contentId}
          onClick={onToggle}
        >
          <ChevronRight
            className={cn(
              "size-3 shrink-0 transition-transform duration-150",
              isOpen && "rotate-90",
            )}
            aria-hidden="true"
          />
          <span className="truncate">{triggerLabel}</span>
        </Button>
        {statusText != null ? (
          <span
            role="status"
            aria-live="polite"
            className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground"
          >
            {statusText}
          </span>
        ) : null}
      </div>
      <div
        id={contentId}
        // `flex flex-col` here (not just `flex-1 overflow-y-auto`) is load
        // -bearing: a `flex-1`-sized scrollable child (e.g. `VisitTranscript`'s
        // line list) only gets a bounded height — and therefore only
        // actually scrolls internally instead of growing to fit its content
        // — when its parent is a flex container. Without it, `flex-1`/
        // `min-h-0` on the child are no-ops and this wrapper's own
        // `overflow-y-auto` scrolls the whole section instead of the child
        // pinning new lines to the bottom.
        className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-3"
        style={{ visibility: isContentVisible ? "visible" : "hidden" }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Pinned strip at the bottom of the agenda region holding the scratch pad
 * and (when a live recording exists) the transcript. Both sections expand
 * independently (not an accordion) as plain inline content — expanding one
 * pushes the agenda list above it to shrink and scroll internally, capped at
 * `DOCK_MAX_HEIGHT_PERCENT` of the agenda column's height so the dock can
 * never crowd out the whole list. See `DOCK_MAX_HEIGHT_PERCENT`'s doc
 * comment for why that cap is a plain CSS percentage rather than a
 * container-query unit.
 *
 * The scratch pad's stateful editor lives in `useAgendaScratchpad`, called
 * here (a component that stays mounted for the whole visit — only its
 * `isScratchpadOpen` state toggles) rather than inside the section body.
 * That keeps autosave state alive across expand/collapse. `AgendaScratchPadView`
 * itself now also stays mounted across the toggle (`DockSection` keeps
 * content in the DOM and hides it with `visibility` so the height change can
 * animate), which is strictly safer than the previous mount/unmount cycle —
 * see `useAgendaScratchpad`'s doc comment for why either shape is fine.
 */
export function AgendaDock({
  encounterId,
  disabled = false,
  transcript,
  transcriptLineCount = 0,
  className,
}: AgendaDockProps) {
  const [isScratchpadOpen, setIsScratchpadOpen] = useState(false);
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const scratchpadContentId = useId();
  const transcriptContentId = useId();
  const scratchpad = useAgendaScratchpad({ encounterId, disabled });

  const hasScratchpad = scratchpad.canLoad;
  const hasTranscript = transcript != null;
  const hasAnySection = hasScratchpad || hasTranscript;

  return (
    <div
      // STITCH-551: with the composer now pinned *above* the dock (see
      // Agenda.tsx), the dock is the true bottom-most element of the pinned
      // region, so it — not the composer — owns the region's bottom
      // padding. This has to live on the dock's own root rather than on a
      // section's content div: sections' collapsed height is 0 content
      // height (the header row consumes the whole declared height), so
      // padding placed there would either paint nothing or, worse, overflow
      // the section's inline `height` and corrupt the collapsed-height
      // measurement `DockSection` depends on. Padding here is safe in both
      // states because it counts toward `DOCK_MAX_HEIGHT_PERCENT`'s cap
      // (border-box), not on top of it.
      //
      // Always rendered, even with neither section present (no encounter
      // yet and no live transcript — the visit page's default landing
      // state), so `pb-5` doesn't disappear along with a `return null` and
      // leave the composer's `border-b` sitting flush against the agenda
      // column's edge. Empty in that state, it's just a plain `<div>` with
      // no border, background, or role of its own — nothing for an a11y
      // tree or a sighted user to notice besides the gap itself.
      className={cn("flex shrink-0 flex-col pb-5", className)}
      data-testid="agenda-dock"
      style={
        hasAnySection ? { maxHeight: `${DOCK_MAX_HEIGHT_PERCENT}%` } : undefined
      }
    >
      {hasScratchpad ? (
        <DockSection
          isOpen={isScratchpadOpen}
          onToggle={() => setIsScratchpadOpen((open) => !open)}
          triggerLabel={SCRATCHPAD_LABEL}
          statusText={scratchpad.status === "saving" ? "Saving…" : undefined}
          contentId={scratchpadContentId}
          preferredHeightPx={
            DOCK_TOGGLE_ROW_HEIGHT_PX + SCRATCHPAD_BODY_PREFERRED_HEIGHT_PX
          }
          withBottomBorder={hasTranscript}
          testId="scratchpad"
        >
          <AgendaScratchPadView {...scratchpad} />
        </DockSection>
      ) : null}
      {hasTranscript ? (
        <DockSection
          isOpen={isTranscriptOpen}
          onToggle={() => setIsTranscriptOpen((open) => !open)}
          triggerLabel={`Transcript (${transcriptLineCount} lines)`}
          contentId={transcriptContentId}
          preferredHeightPx={
            DOCK_TOGGLE_ROW_HEIGHT_PX + TRANSCRIPT_BODY_PREFERRED_HEIGHT_PX
          }
          testId="transcript"
        >
          {transcript}
        </DockSection>
      ) : null}
    </div>
  );
}
